/**
 * SourceRowCellData — sheet-level registry of per-row cell stores.
 *
 * Thin wrapper over `SourceRow.CellDataStore<RowLocalId>` that
 * translates the generic store's in-process `CellDataChange<K>`
 * events into the sheet's invalidation vocabulary
 * (`ColumnDataChanged(rowId, date)`) and forwards them through the
 * shared `InvalidationSink`.
 *
 * The underlying `CellDataStore<K>` is the same class used for the
 * Client-scoped canonical store (`CellDataStore<string>`); the two
 * instantiations differ only in key type and whether a capacity
 * bound is set: provider-backed cells are shared across sheets, so the
 * Client-scoped store is bounded; a sheet's own store is not.
 *
 * Per-row indexing unlocks:
 *   - O(log n + k) range queries (via `RowCellData`'s `SortedDeque`).
 *   - cardinality-aware routing (single rows use the sorted date
 *     index; multi rows use a cell-id map).
 *
 * Match lona-js's event vocabulary — set and clear both surface as
 * `ColumnDataChanged`; consumers inspect the store for the
 * post-mutation value.
 */

import type { Option } from "../core/option.ts";
import { type DateTime, NaiveDate, type Utc } from "@tento-chrono";
import { DependencyAspect } from "@tento-core/dependency-graph";
import type { LedgerReader } from "@tento-core/ledger";
import { ListenerErrors, type ListenerErrorSink } from "@tento-core/listener-errors";

import type { CellEntry, CellStyle, CellValue } from "../core/dtype.ts";
import {
  Actor,
  ColumnDataChanged,
  type InvalidationSink,
} from "../tx/invalidation.ts";
import { cellLedgerBump, LedgerKeys } from "./ledger-keys.ts";
import type { CellLedgerBinding, LedgerBumper, LedgerShadowPort, LedgerSyntheticPort } from "./ledger-keys.ts";
import type { RenderSkip } from "../render/ledger-render-skip.ts";
import { RenderCursors } from "../render/render-cursor.ts";
import type { RenderFlags } from "../render/render-flags.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import { AddressOf } from "../ids/address-of.ts";
import { RowCellData } from "./row-cell-data.ts";
import type { CellCardinality } from "../core/dtype.ts";
import {
  type CellDataBatchHandler,
  type CellDataChange,
  type CellDataChangeHandler,
  CellDataStore,
  type StoreInstallObserver,
} from "./source-row-cell-data-store.ts";
import type { Unsubscribe } from "../backends/rows-backend.ts";
import { CellId } from "../ids/ids.ts";

/**
 * The `ColumnDataChanged` that announces one store change for `rowId`: a
 * date-keyed cell's date; a datetime (custom) cell's start's UTC date (the
 * date it is stored under, the key a row's events facade re-reads by),
 * carrying the cell's instants (`ColumnDataChanged.timed`), in which a
 * viewer's paint surface finds its own day columns (07:00 in Asia/Tokyo is
 * the previous UTC date). One event per change either way.
 */
export function columnDataChangedOf<K>(change: CellDataChange<K>, rowId: RowLocalId, actor: Actor): ColumnDataChanged {
  switch (change.kind) {
    case "set":
      return new ColumnDataChanged(actor, change.entry.date, rowId);
    case "clear":
      return new ColumnDataChanged(actor, change.date, rowId);
    case "set-custom":
      return ColumnDataChanged.timed(actor, NaiveDate.Partial.fromDate(change.entry.dtr.start.date), rowId, change.entry.dtr);
    case "clear-custom":
      return ColumnDataChanged.timed(actor, NaiveDate.Partial.fromDate(change.dtr.start.date), rowId, change.dtr);
  }
}

export class SourceRowCellData<Row = never, Sheet = never> {
  static readonly ACTOR = new Actor("SourceRowCellData");

  readonly #store: CellDataStore<RowLocalId>;
  #ordinary: Option<CellDataStore<RowLocalId>> = null;
  readonly #derivedRows = new Map<RowLocalId, boolean>();
  readonly #seededRows = new Set<RowLocalId>();
  readonly #rowPins = new Map<RowLocalId, Unsubscribe>();
  readonly #leases = new Map<number, { ordinary: number; derived: number; releaseOrdinary: Option<Unsubscribe>; releaseDerived: Option<Unsubscribe> }>();
  #nextLease = 0;
  #releaseOrdinary: Option<Unsubscribe> = null;

  /**
   * The shared `LedgerBumper` (or `null` for standalone / test stores).
   * Exposed so the sheet's non-cell ledger write points that reach a source
   * row's store — the `SourceRow` viewport-range commit — can bump without a
   * separate ledger handle on the frozen `Sheet`. Cell bumps happen inside the
   * inner `CellDataStore`; do NOT add cell bumps in this wrapper (double-count).
   */
  readonly ledgerBumper: Option<LedgerBumper>;

  /**
   * The per-sheet `LedgerBinding` (or `null` for standalone / test stores). Held
   * so the LR6 render-skip probe (`currentVersions`, row-identity ledger L5) can
   * resolve a row's OUTPUT `data:` node through `resolveOutputAddress` — the
   * same classification the cell-write bump uses, so a data row's compare node
   * is guaranteed identical to the bumped node and a placement-only row reads
   * `null` (nothing to compare — never skip). Only the ledger-key resolution
   * reads it; cell writes bump inside the inner `CellDataStore` via
   * `LedgerBinding.cellBump`.
   */
  readonly ledgerBinding: Option<CellLedgerBinding>;

  /**
   * The per-sheet ledger shadow (row-identity ledger, L3), or `null` for
   * standalone / test stores. This is the reachability surface: the recompute
   * seams (`ComputedDataSource.recompute`/`ensureLoaded`,
   * `FormulaDataSource.recomputeExpression`) reach the shadow through
   * `this.sheet.stores.sourceCellData.ledgerShadow` and MUST NOT value-import
   * `ledger-shadow` themselves (TDZ — see that module's header). Kept dumb: this
   * wrapper only holds the handle; declarations happen at the seams.
   */
  readonly ledgerShadow: Option<LedgerShadowPort<Row>>;

  /**
   * The per-sheet synthetic-edge declarer (row-identity ledger, L4), or `null`
   * for standalone / test stores. Same reachability surface as `ledgerShadow`:
   * the reexpand seam (`wireDeclaredReexpand` in `alias-templates.ts`) reaches it
   * through `this.sheet.stores.sourceCellData.ledgerSynthetic` **type-only** and
   * MUST NOT value-import `ledger-synthetic` (TDZ — see that module's header).
   * Declarations happen at hydrate (`syncAll`) + the reexpand seam.
   */
  readonly ledgerSynthetic: Option<LedgerSyntheticPort<Sheet>>;

  /**
   * The per-sheet render-cursor registry (row-identity ledger, L5 promotion), or
   * `null` for standalone / test stores (no ledger ⇒ nothing to compare against).
   * THIS store is the registry's `RenderSkip.AvailabilityProbe` — minting here
   * (not in `raw-sheet`) keeps the probe wiring non-circular: the cursor resolves a row's
   * OUTPUT versions through `this.currentVersions`, the SAME node the cell-write
   * bump moves. Paint surfaces (DOM binder, CLI TUI) reach it via
   * `stores.sourceCellData.renderCursors` and mint one cursor each; the reconcile
   * subscriptions are wired at the end of `RawSheet.hydrate`. Consumers on
   * ledger-less sheets `?.`-no-op.
   */
  readonly renderCursors: Option<RenderCursors>;

  readonly #sink: InvalidationSink;
  constructor(
    sink: InvalidationSink,
    binding: Option<CellLedgerBinding>,
    shadow: Option<LedgerShadowPort<Row>>,
    synthetic: Option<LedgerSyntheticPort<Sheet>>,
    /** The sheet's render-pipeline flags (`SheetRegistries.render`): the LR6 skip's default switch. */
    render: RenderFlags,
    /** Where the sheet's own store reports a throwing subscriber: its client's sink (`withOrdinary`), else `RETHROW`. */
    listenerErrors: ListenerErrorSink = ListenerErrors.RETHROW,
  ) {
    this.#sink = sink;
    this.ledgerBumper = binding?.bumper ?? null;
    this.ledgerBinding = binding ?? null;
    this.ledgerShadow = shadow ?? null;
    this.ledgerSynthetic = synthetic ?? null;
    // Mint the render-cursor registry only when a ledger is attached — the probe
    // (this store's `currentVersions`) reads real version nodes; standalone /
    // test stores have nothing to compare, so the handle stays null.
    this.renderCursors = binding != null ? RenderCursors.make(this, render) : null;
    this.#store = new CellDataStore<RowLocalId>(
      binding != null ? { cellBump: cellLedgerBump(binding), listenerErrors } : { listenerErrors },
    );
    // Translate the generic store's change events into the sheet's
    // invalidation vocabulary. `init` bypasses subscribers, so
    // hydrate seeding stays silent as before.
    this.#store.subscribeAll((change) => {
      // Datetime (custom) cells carry a `dtr`: invalidate the column its
      // start is stored under, carrying its instants (`columnDataChangedOf`).
      // Use the originator's actor when supplied; otherwise fall back to
      // the store's own actor. The bridge in lona-js reads
      // `event.actor` to stamp `RowCellCache._fetched` via
      // `applyCellWriteLocal`, which surfaces as `change.actor` on
      // plugin `CellHandle.onData` callbacks. Untagged writes (sync
      // replay, formula compute) keep the legacy `SourceRowCellData.ACTOR`
      // signature so existing consumers see no behavioural change.
      const actor = change.actor ?? SourceRowCellData.ACTOR;
      this.#sink.emit(columnDataChangedOf(change, change.key, actor));
    });
  }

  /** SDK composition: ordinary facts are shared; derived output and render state stay here. */
  static withOrdinary<Row, Sheet>(
    sink: InvalidationSink, binding: Option<CellLedgerBinding>, shadow: Option<LedgerShadowPort<Row>>,
    synthetic: Option<LedgerSyntheticPort<Sheet>>, render: RenderFlags, ordinary: CellDataStore<RowLocalId>,
  ): SourceRowCellData<Row, Sheet> {
    // The sheet's own (derived) store reports where the client's ordinary store does.
    const data = new SourceRowCellData<Row, Sheet>(sink, binding, shadow, synthetic, render, ordinary.listenerErrors);
    data.#ordinary = ordinary;
    // One relay per (ordinary store, sink), counted by the store that owns it.
    data.#releaseOrdinary = ordinary.sharedSubscription(sink, () =>
      ordinary.subscribeAll(change => {
        sink.emit(columnDataChangedOf(change, change.key, change.actor ?? SourceRowCellData.ACTOR));
      }));
    return data;
  }

  /**
   * Declare who owns `rowId`'s cells, from the actual source variant, before
   * cardinality or seeding: `"derived"` (an engine computes them here) or
   * `"stored"` (the ordinary, shared facts).
   */
  declareCellOwnership(rowId: RowLocalId, ownership: "derived" | "stored"): void {
    const derived = ownership === "derived";
    const prior = this.#derivedRows.get(rowId);
    if (prior != null && prior !== derived) throw new Error("SourceRowCellData: cell ownership changed while attached");
    if (prior == null && !derived && this.#ordinary?.hasAdmitted(rowId)) this.#seededRows.add(rowId);
    this.#derivedRows.set(rowId, derived);
    if (!derived && this.#ordinary != null && !this.#rowPins.has(rowId)) this.#rowPins.set(rowId, this.#ordinary.retain(rowId));
  }

  /** Whether `value` is one of this sheet's cell stores' own copies (`CellDataStore.ownsCellValue`). */
  ownsCellValue(value: object): boolean {
    return this.#store.ownsCellValue(value) || (this.#ordinary?.ownsCellValue(value) ?? false);
  }

  /** SDK-only: stable authority for an attached ordinary source, not its projection. */
  ordinaryStore(rowId: RowLocalId): Option<CellDataStore<RowLocalId>> {
    return this.#derivedRows.get(rowId) === false ? this.#ordinary : null;
  }

  /** Release this sheet's observation, retaining shared ordinary facts. */
  dispose(): void {
    this.#releaseOrdinary?.(); this.#releaseOrdinary = null;
    for (const release of this.#rowPins.values()) release();
    this.#rowPins.clear();
  }
  /** Placement detach releases its hold and keeps facts another holder still owns. */
  detach(rowId: RowLocalId): void {
    this.#rowPins.get(rowId)?.(); this.#rowPins.delete(rowId);
  }

  #forRow(rowId: RowLocalId): CellDataStore<RowLocalId> {
    if (this.#ordinary == null) return this.#store;
    return this.#derivedRows.get(rowId) === false ? this.#ordinary : this.#store;
  }

  /**
   * Declare a row's cell cardinality. Call once per source row at
   * hydrate time — determines whether the underlying `RowCellData`
   * uses the sorted-date index (`single`) or the cell-id map
   * (`multi`). Rows that skip this call default to `single`.
   */
  /** Read the declaration from the actual ordinary or private derived lane. */
  declaredCardinality(rowId: RowLocalId): Option<CellCardinality> {
    return this.#forRow(rowId).declaredCardinality(rowId);
  }

  setCardinality(rowId: RowLocalId, cardinality: CellCardinality): void {
    this.#forRow(rowId).setCardinality(rowId, cardinality);
  }

  /** Read one cell. Returns `null` if unset. `V` narrows at call site. */
  get(
    rowId: RowLocalId,
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
  ): Option<CellValue> {
    return this.#forRow(rowId).get(rowId, date, cellId);
  }

  /** Read all cells for a row, optionally filtered to a `NaiveDate.Range`. */
  cells(
    rowId: RowLocalId,
    range: Option<NaiveDate.Range>,
  ): readonly CellEntry[] {
    return this.#forRow(rowId).cells(rowId, range);
  }

  /** The count of a row's date-keyed and timepoint cells, without listing them. */
  cellCount(rowId: RowLocalId): number {
    return this.#forRow(rowId).cellCount(rowId);
  }

  /** Read datetime (custom) cells for a row, optionally range-filtered. */
  customCells(
    rowId: RowLocalId,
    range: Option<DateTime.Range<Utc>>,
  ): readonly CellEntry.Timepoint[] {
    return this.#forRow(rowId).customCells(rowId, range);
  }

  /**
   * Upsert one datetime (custom) cell. Emits `set-custom`; `actor`
   * tags the originator for self-echo suppression.
   */
  setCustom(
    rowId: RowLocalId,
    entry: CellEntry.Timepoint,
    actor: Option<Actor>,
  ): void {
    this.#forRow(rowId).setCustom(rowId, entry, actor);
  }

  /** Delete one datetime cell by raw id. Emits `clear-custom` if present. */
  removeCustom(rowId: RowLocalId, id: string, actor: Option<Actor>): void {
    this.#forRow(rowId).removeCustom(rowId, id, actor);
  }

  /** Replace datetime cells in `range` with `entries` (server-truth
   *  reconcile). Emits `set-custom` per surviving entry. */
  replaceCustomInRange(
    rowId: RowLocalId,
    range: DateTime.Range<Utc>,
    entries: readonly CellEntry.Timepoint[],
    actor: Option<Actor>,
  ): void {
    this.#forRow(rowId).replaceCustomInRange(rowId, range, entries, actor);
  }

  /**
   * Write one cell. Emits `ColumnDataChanged(actor, date, rowId)` —
   * the supplied `actor` propagates onto the invalidation so the
   * lona-js bridge can stamp `RowCellCache._fetched` and plugins can
   * suppress self-echo via `change.actor`. Pass `null` for untagged
   * writes (formula compute, sync replay). `style` carries the
   * optional per-cell style bag populated by formula `cell.bg`
   * calls — see `formula-typed-outputs.md` §3.
   */
  set(
    rowId: RowLocalId,
    date: NaiveDate.Partial,
    value: CellValue,
    cellId: Option<CellId>,
    style: Option<CellStyle>,
    actor: Option<Actor>,
  ): void {
    this.#forRow(rowId).set(rowId, date, value, cellId, style, actor);
  }

  /**
   * Seed one cell without emitting. Used by `RawSheet.hydrate` to
   * populate cell data from the wire format — subscribers attached
   * before hydrate would otherwise see N spurious seed events.
   */
  init(
    rowId: RowLocalId,
    date: NaiveDate.Partial,
    value: CellValue,
    cellId: Option<CellId>,
    style: Option<CellStyle>,
  ): void {
    const store = this.#forRow(rowId);
    if (store === this.#ordinary) {
      if (this.#seededRows.has(rowId)) return;
      store.asInstall(() => store.set(rowId, date, value, cellId, style, null));
    } else store.init(rowId, date, value, cellId, style);
  }

  /** Clear one cell. Emits `ColumnDataChanged(actor, date, rowId)`
   *  (even if absent). Pass the originator's `actor` for self-echo
   *  attribution, or `null` for untagged clears. */
  clear(
    rowId: RowLocalId,
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
    actor: Option<Actor>,
  ): void {
    this.#forRow(rowId).clear(rowId, date, cellId, actor);
  }

  /** Access the underlying per-row store. Exposed for low-level callers. */
  rowData(rowId: RowLocalId): Option<RowCellData> {
    return this.#forRow(rowId).rowData(rowId);
  }

  /**
   * The LR6 render-skip probe (row-identity ledger L5): the CURRENT versions of
   * `rowId`'s OUTPUT ledger nodes — its own `data:<Address>` cell node and its
   * `attr:<serial>` bag node, both at the `VALUES` facet. `null` when no ledger
   * is attached (standalone / test store), **or when the row classifies
   * placement-only** (`LedgerBinding.resolveOutputAddress` → `null`): such a row
   * owns no data node, so there is nothing to compare and `RenderSkip.decide`
   * must never skip it. (Resolving through the write path's total resolver here
   * would compare against its fallback `data:local:<serial>` — a node nothing
   * ever writes, i.e. a frozen 0 that reads "unchanged" forever; that phantom
   * compare was the 08-06 header zero-height break and the 08-07 create-does-
   * not-paint demote.) For data rows the key is resolved through the same
   * classification the cell-write bump uses, so the compare node is exactly the
   * node L2 bumps when the row's cells are written — never an upstream input.
   * Reads through `LedgerReader` only (no write surface).
   *
   * Seam 12 keeps this signature verbatim for the consumers that already hold it
   * (sheets-ui's live scene runner, the LR6 probe fixtures) and moves the
   * three-way answer to `currentAvailability`, which this now narrows.
   */
  currentVersions(rowId: RowLocalId): Option<RenderSkip.Versions> {
    // Narrowed here rather than through `RenderSkip.versionsOf` so this module
    // keeps its TYPE-only edge to `ledger-render-skip` (that module's header
    // pins its TDZ-inertness; a value import would add a second live edge).
    const availability = this.currentAvailability(rowId);
    return availability.kind === "versions"
      ? { values: availability.values, attr: availability.attr }
      : null;
  }

  /**
   * **Seam 12** — the same read as `currentVersions`, but saying WHICH of the
   * three cases holds instead of collapsing two of them into one `null`:
   *
   *  - `no-ledger` — this store was built without a `LedgerBinding` (standalone
   *    / test store, or a sheet hydrated with no bumper). No node versions
   *    anything here, so no compare is meaningful.
   *  - `placement-only` — a ledger IS attached, and it classified this row as
   *    owning no data node (`LedgerBinding.resolveOutputAddress` → `null`). A
   *    permanent property of the row, not of the wiring.
   *  - `versions` — the row's own `data:<Address>` + `attr:<serial>` versions,
   *    read at the `VALUES` facet through `LedgerReader` only (no write surface).
   *
   * The distinction is load-bearing for attribution: both kinds refuse the skip,
   * but only one of them is a *finding* (a ledger that should have been wired
   * and was not). `RenderSkip.decide` records the kind on its `Decision`.
   */
  currentAvailability(rowId: RowLocalId): RenderSkip.Availability {
    const binding = this.ledgerBinding;
    const bumper = this.ledgerBumper;
    if (binding == null || bumper == null) return { kind: "no-ledger" };
    const address = this.#ordinary != null && this.#derivedRows.get(rowId) === false
      ? AddressOf.forLocalId(rowId) : binding.resolveOutputAddress(rowId);
    if (address == null) return { kind: "placement-only" };
    const reader: LedgerReader<NaiveDate.Range> = bumper.ledger;
    const dataKey = LedgerKeys.data(address);
    const attrKey = LedgerKeys.attr(rowId.serial);
    return {
      kind: "versions",
      values: reader.version(dataKey, DependencyAspect.VALUES),
      attr: reader.version(attrKey, DependencyAspect.VALUES),
    };
  }

  /**
   * Companion to `currentVersions` (row-identity ledger L5): the string form of
   * `rowId`'s resolved `data:` OUTPUT node key — the SAME node `currentVersions`
   * versions and the cell-write bump moves, via the identical
   * `LedgerBinding.resolveOutputAddress`. `null` when no ledger is attached
   * (standalone / test store) or the row classifies placement-only (no data
   * node — mirrors `currentVersions`). Additive + read-only; the live scene
   * runner stamps this as a cell node's `address` so the parity classifier can
   * detect canonical share — two rows resolving to ONE `data:` node co-move.
   */
  dataAddress(rowId: RowLocalId): Option<string> {
    const binding = this.ledgerBinding;
    if (binding == null) return null;
    const address = this.#ordinary != null && this.#derivedRows.get(rowId) === false
      ? AddressOf.forLocalId(rowId) : binding.resolveOutputAddress(rowId);
    if (address == null) return null;
    return String(LedgerKeys.data(address));
  }

  /**
   * Subscribe to every cell mutation with the FULL `CellDataChange`
   * payload (kind set/clear, the entry with its cell id, the raw
   * originating actor — `null` when untagged). Unlike the
   * `ColumnDataChanged` translation above, this carries enough to
   * mirror multi-cardinality writes and clears into per-row caches
   * (the lona-js store→handle bridge). Returns an unsubscribe.
   */
  subscribeAll(
    handler: CellDataChangeHandler<RowLocalId>,
  ): Unsubscribe {
    const derived = this.#store.subscribeAll(handler);
    const ordinary = this.#ordinary?.subscribeAll(change => {
      // Logical subscribers (formula coverage) observe this projection's
      // ordinary inputs; detached and unrelated client rows do not belong here.
      if (this.#rowPins.has(change.key)) handler(change);
    }) ?? null;
    return () => { derived(); ordinary?.(); };
  }

  /** Bracket a burst of writes — see `CellDataStore.batch`. */
  batch<T>(fn: () => T): T {
    return this.#ordinary == null ? this.#store.batch(fn) : this.#ordinary.batch(() => this.#store.batch(fn));
  }

  /** See `CellDataStore.writeRevision`. */
  get writeRevision(): number {
    return this.#store.writeRevision;
  }

  /** See `CellDataStore.asInstall`. */
  asInstall<T>(fn: () => T): T {
    return this.#ordinary == null ? this.#store.asInstall(fn) : this.#ordinary.asInstall(() => this.#store.asInstall(fn));
  }

  /** See `CellDataStore.observeInstalls`. */
  observeInstalls(observer: StoreInstallObserver): Unsubscribe {
    if (this.#ordinary == null) return this.#store.observeInstalls(observer);
    let depth = 0;
    let finish: void | (() => void);
    const joined: StoreInstallObserver = () => {
      if (depth === 0) finish = observer();
      depth++;
      return () => {
        if (--depth === 0) { const done = finish; finish = undefined; done?.(); }
      };
    };
    const derived = this.#store.observeInstalls(joined);
    const ordinary = this.#ordinary.observeInstalls(joined);
    return () => { derived(); ordinary(); };
  }

  /** See `CellDataStore.writtenLocallySince`. */
  writtenLocallySince(
    rowId: RowLocalId,
    since: number,
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
  ): boolean {
    if (this.#ordinary == null) return this.#store.writtenLocallySince(rowId, since, date, cellId);
    const lease = this.#leases.get(since);
    if (lease == null) throw new Error("SourceRowCellData: unknown read lease");
    const store = this.#forRow(rowId);
    return store.writtenLocallySince(rowId, store === this.#ordinary ? lease.ordinary : lease.derived, date, cellId);
  }

  /** See `CellDataStore.customWrittenLocallySince`. */
  customWrittenLocallySince(rowId: RowLocalId, since: number, id: string): boolean {
    if (this.#ordinary == null) return this.#store.customWrittenLocallySince(rowId, since, id);
    const lease = this.#leases.get(since);
    if (lease == null) throw new Error("SourceRowCellData: unknown read lease");
    const store = this.#forRow(rowId);
    return store.customWrittenLocallySince(rowId, store === this.#ordinary ? lease.ordinary : lease.derived, id);
  }

  /** See `CellDataStore.captureRevision` / `releaseRevision`. */
  captureRevision(): number {
    if (this.#ordinary == null) return this.#store.captureRevision();
    const token = ++this.#nextLease;
    this.#leases.set(token, { ordinary: this.#ordinary?.captureRevision() ?? 0, derived: this.#store.captureRevision(), releaseOrdinary: null, releaseDerived: null });
    return token;
  }

  /**
   * Keyed read admission in the actual ordinary/private lane. Composed
   * facades return an opaque paired-clock token, never an underlying revision.
   * Empty-key holds capture the other clock without retaining unrelated rows.
   */
  captureRead(rowId: RowLocalId): {
    readonly revision: number;
    readonly resident: RowCellData;
    readonly current: () => boolean;
    readonly release: Unsubscribe;
  } {
    const authority = this.#forRow(rowId);
    const read = authority.captureRead(rowId);
    if (this.#ordinary == null) return read;
    let other: Option<{ readonly revision: number; readonly release: Unsubscribe }> = null;
    try {
      const ordinary = authority === this.#ordinary;
      other = (ordinary ? this.#store : this.#ordinary).holdRevision([]);
      const token = ++this.#nextLease;
      this.#leases.set(token, {
        ordinary: ordinary ? read.revision : other.revision,
        derived: ordinary ? other.revision : read.revision,
        releaseOrdinary: ordinary ? read.release : other.release,
        releaseDerived: ordinary ? other.release : read.release,
      });
      return {
        revision: token,
        resident: read.resident,
        current: () => this.#leases.has(token) && this.#forRow(rowId) === authority && read.current(),
        release: () => this.releaseRevision(token),
      };
    } catch (error) {
      try { read.release(); }
      finally {
        try { other?.release(); } finally { throw error; }
      }
    }
  }

  /** Capture the seed's exact clocks while retaining only this projection's ordinary rows. */
  captureSeedRevision(): number {
    if (this.#ordinary == null) return this.#store.captureRevision();
    const keys = [...this.#derivedRows].filter(([, derived]) => !derived).map(([key]) => key);
    const held = this.#ordinary.holdRevision(keys);
    const token = ++this.#nextLease;
    this.#leases.set(token, { ordinary: held.revision, derived: this.#store.captureRevision(), releaseOrdinary: held.release, releaseDerived: null });
    return token;
  }

  releaseRevision(rev: number): void {
    if (this.#ordinary == null) { this.#store.releaseRevision(rev); return; }
    const lease = this.#leases.get(rev);
    if (lease == null) return;
    this.#leases.delete(rev);
    try {
      if (lease.releaseOrdinary != null) lease.releaseOrdinary();
      else this.#ordinary?.releaseRevision(lease.ordinary);
    } finally {
      if (lease.releaseDerived != null) lease.releaseDerived();
      else this.#store.releaseRevision(lease.derived);
    }
  }

  /** See `CellDataStore.isInstalling`. */
  get isInstalling(): boolean {
    return this.#store.isInstalling;
  }

  /** Install provenance of a row's physical ordinary or private derived lane. */
  isInstallingFor(rowId: RowLocalId): boolean {
    return this.#forRow(rowId).isInstalling;
  }

  /** @internal Test hook — see `CellDataStore.localWriteCount`. */
  localWriteCount(): number {
    return this.#store.localWriteCount();
  }

  /** Observe `batch` boundaries — see `CellDataStore.subscribeBatch`. */
  subscribeBatch(handler: CellDataBatchHandler): Unsubscribe {
    if (this.#ordinary == null) return this.#store.subscribeBatch(handler);
    let depth = 0;
    const joined: CellDataBatchHandler = {
      begin: () => { if (depth++ === 0) handler.begin(); },
      end: () => { if (--depth === 0) handler.end(); },
    };
    const derived = this.#store.subscribeBatch(joined);
    const ordinary = this.#ordinary.subscribeBatch(joined);
    return () => { derived(); ordinary(); };
  }

  // ── Staleness ───────────────────────────────────────────────────
  //
  // Delegate the generic store's staleness API. The formula engine
  // uses `hasAnyStale` / `isStale` to short-circuit
  // `recomputeDependents` when the range has no writes pending
  // consumption; `markFresh` closes the cycle after a successful
  // recompute. See `source-row-cell-data-store.ts` for semantics.

  isStale(
    rowId: RowLocalId,
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
  ): boolean {
    return this.#forRow(rowId).isStale(rowId, date, cellId);
  }

  hasAnyStale(rowId: RowLocalId): boolean {
    return this.#forRow(rowId).hasAnyStale(rowId);
  }

  anyStaleInRange(rowId: RowLocalId, range: NaiveDate.Range): boolean {
    return this.#forRow(rowId).anyStaleInRange(rowId, range);
  }

  markStale(
    rowId: RowLocalId,
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
  ): void {
    this.#forRow(rowId).markStale(rowId, date, cellId);
  }

  markFresh(
    rowId: RowLocalId,
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
  ): void {
    this.#forRow(rowId).markFresh(rowId, date, cellId);
  }

  markFreshInRange(rowId: RowLocalId, range: NaiveDate.Range): void {
    this.#forRow(rowId).markFreshInRange(rowId, range);
  }

  markAllFresh(rowId: RowLocalId): void {
    this.#forRow(rowId).markAllFresh(rowId);
  }
}
